import { z } from 'zod';

/**
 * CV + target-role (JD) contracts. PRE-92 (Phase 8 — CV + JD targeted prep).
 *
 * Split into three concerns:
 *  - LLM output contracts (`cvExtractionSchema`, `jdExtractionSchema`) — validated
 *    against the model's structured JSON, mirrored by the API's parsers.
 *  - Request DTOs (`createTargetRoleInputSchema`) — inbound validation.
 *  - View shapes (`cvDocumentViewSchema`, `targetRoleViewSchema`) — API responses.
 */

/** Coarse seniority buckets shared by CV + JD extraction. */
export const SENIORITY_LEVELS = ['junior', 'mid', 'senior', 'lead', 'unknown'] as const;
export type SeniorityLevel = (typeof SENIORITY_LEVELS)[number];

// Upload limits (also enforced server-side on the multipart body).
export const CV_MAX_BYTES = 5 * 1024 * 1024; // 5 MB
export const CV_ACCEPTED_MIME = 'application/pdf';

export const CV_DOCUMENT_STATES = ['uploaded', 'parsing', 'parsed', 'failed'] as const;
export type CvDocumentStatus = (typeof CV_DOCUMENT_STATES)[number];

export const TARGET_ROLE_STATES = ['pending', 'parsing', 'parsed', 'failed'] as const;
export type TargetRoleStatus = (typeof TARGET_ROLE_STATES)[number];

/** How the JD was provided: pasted text, an uploaded PDF, or a screenshot. */
export const TARGET_ROLE_SOURCES = ['text', 'pdf', 'image'] as const;
export type TargetRoleSource = (typeof TARGET_ROLE_SOURCES)[number];

// JD upload limits (PDF or screenshot), enforced server-side on the multipart body.
export const JD_MAX_BYTES = 5 * 1024 * 1024; // 5 MB
export const JD_PDF_MIME = 'application/pdf';
export const JD_IMAGE_MIMES = ['image/png', 'image/jpeg', 'image/webp'] as const;
export const JD_ACCEPTED_MIMES = [JD_PDF_MIME, ...JD_IMAGE_MIMES] as const;
export type JdImageMime = (typeof JD_IMAGE_MIMES)[number];

/** Structured data the LLM extracts from a CV. */
export const cvExtractionSchema = z.object({
  summary: z.string(),
  seniority: z.enum(SENIORITY_LEVELS),
  yearsExperience: z.number().nullable(),
  skills: z.array(z.string()),
  technologies: z.array(z.string()),
  roles: z.array(
    z.object({
      title: z.string(),
      company: z.string().nullable(),
      durationText: z.string().nullable(),
    }),
  ),
});

export type CvExtraction = z.infer<typeof cvExtractionSchema>;

/** Structured data the LLM extracts from a job description. */
export const jdExtractionSchema = z.object({
  roleTitle: z.string(),
  seniority: z.enum(SENIORITY_LEVELS),
  requiredSkills: z.array(z.string()),
  niceToHaveSkills: z.array(z.string()),
  keyTechnologies: z.array(z.string()),
  responsibilities: z.array(z.string()),
});

export type JdExtraction = z.infer<typeof jdExtractionSchema>;

/**
 * CV ↔ JD gap analysis. PRE-95 (Phase 8).
 *
 * One entry per JD requirement compared against the candidate's CV:
 *  - `status`   — is the skill already `covered`, only partially there (`weak`),
 *                 or entirely `missing` from the CV?
 *  - `severity` — how important is closing it (drives the roadmap weighting).
 *  - `skill_slug` — optional mapping to a real practice skill so the generated
 *                 roadmap items can spawn a skill-scoped `roadmap_practice`
 *                 session. Only slugs from the known-skill list are trusted.
 */
export const GAP_STATUSES = ['missing', 'weak', 'covered'] as const;
export type GapStatus = (typeof GAP_STATUSES)[number];

export const GAP_SEVERITIES = ['high', 'medium', 'low'] as const;
export type GapSeverity = (typeof GAP_SEVERITIES)[number];

export const cvJdGapSchema = z.object({
  skill: z.string().min(1).max(160),
  status: z.enum(GAP_STATUSES),
  severity: z.enum(GAP_SEVERITIES),
  reason: z.string().min(1).max(500),
  skill_slug: z.string().min(1).max(120).nullish(),
});
export type CvJdGap = z.infer<typeof cvJdGapSchema>;

export const cvJdGapAnalysisSchema = z.object({
  /** One-line framing, e.g. "Strong React background but the JD centres on Angular." */
  summary: z.string().min(1).max(600),
  gaps: z.array(cvJdGapSchema),
});
export type CvJdGapAnalysis = z.infer<typeof cvJdGapAnalysisSchema>;

/** Strategy recorded on a role-targeted gap analysis — which engine produced it. */
export const GAP_STRATEGIES = ['heuristic_v0', 'llm_v1'] as const;
export const gapStrategySchema = z.enum(GAP_STRATEGIES);
export type GapStrategy = z.infer<typeof gapStrategySchema>;

/**
 * POST /v1/job-applications/:id/roadmap response — the CV↔JD gap list plus a
 * pointer to the role-targeted roadmap generated from it (PRE-95).
 */
export const roleRoadmapResponseSchema = z.object({
  gap_analysis: cvJdGapAnalysisSchema,
  gap_strategy: gapStrategySchema,
  roadmap: z.object({
    id: z.string(),
    item_count: z.number(),
    strategy: z.string(),
  }),
});
export type RoleRoadmapResponse = z.infer<typeof roleRoadmapResponseSchema>;

/**
 * POST /v1/job-applications body (JSON / pasted text). `jdText` must be non-empty
 * (graceful "boş JD"). Role title is optional (PRE-269) — the server falls back
 * to JD extraction or "Untitled role". The same endpoint also accepts
 * multipart/form-data with a `file` (PDF or screenshot) — validated server-side,
 * not by this schema.
 */
export const createTargetRoleInputSchema = z.object({
  company: z.string().trim().max(200).optional(),
  roleTitle: z.string().trim().min(1).max(200).optional(),
  jdText: z.string().trim().min(1).max(20000),
  cvDocumentId: z.string().min(1).optional(),
});

export type CreateTargetRoleInput = z.infer<typeof createTargetRoleInputSchema>;

/**
 * PATCH /v1/job-applications/:id — all fields optional; at least one required.
 * `cvDocumentId: null` unlinks the CV. Empty `company` clears it.
 */
export const updateTargetRoleInputSchema = z
  .object({
    company: z.string().trim().max(200).optional(),
    roleTitle: z.string().trim().min(1).max(200).optional(),
    jdText: z.string().trim().min(1).max(20000).optional(),
    cvDocumentId: z.string().min(1).nullable().optional(),
  })
  .refine(
    (value) =>
      value.company !== undefined ||
      value.roleTitle !== undefined ||
      value.jdText !== undefined ||
      value.cvDocumentId !== undefined,
    { message: 'at least one field is required' },
  );

export type UpdateTargetRoleInput = z.infer<typeof updateTargetRoleInputSchema>;

/** CV list/detail view. `extraction` is present only when status = 'parsed'. */
export const cvDocumentViewSchema = z.object({
  id: z.string(),
  file_name: z.string(),
  mime_type: z.string(),
  size_bytes: z.number(),
  status: z.enum(CV_DOCUMENT_STATES),
  parse_error: z.string().nullable(),
  extraction: cvExtractionSchema.nullable(),
  created_at: z.string(),
});

export type CvDocumentView = z.infer<typeof cvDocumentViewSchema>;

/** Target-role list/detail view. `extraction` present only when status = 'parsed'. */
export const targetRoleViewSchema = z.object({
  id: z.string(),
  company: z.string().nullable(),
  role_title: z.string(),
  jd_text: z.string(),
  source: z.enum(TARGET_ROLE_SOURCES),
  cv_document_id: z.string().nullable(),
  status: z.enum(TARGET_ROLE_STATES),
  parse_error: z.string().nullable(),
  extraction: jdExtractionSchema.nullable(),
  /** Last CV↔JD gap analysis for this role (PRE-95); null until one is run. */
  gap_analysis: cvJdGapAnalysisSchema.nullable(),
  created_at: z.string(),
});

export type TargetRoleView = z.infer<typeof targetRoleViewSchema>;

/** GET /v1/job-applications — owned roles, newest first. */
export const targetRoleListResponseSchema = z.object({
  items: z.array(targetRoleViewSchema),
});

export type TargetRoleListResponse = z.infer<typeof targetRoleListResponseSchema>;

/**
 * PRE-96: one rated skill from the learner's CV.
 *
 * `approved` is the whole point: the rating is a suggestion an LLM made from a
 * claim, and nothing in the product may treat it as settled until the learner
 * says so. `skillId` is null when the catalog does not cover the skill — the row
 * still exists, which is what keeps the gap visible to the merge queue.
 *
 * PRE-133: `rating` is null when NOBODY rated it — the rating call failed, so
 * the row carries the learner's own words and no judgement. A client must show
 * that differently from a 1: one is "we don't know", the other is "you barely
 * know this", and asking someone to approve the second when we meant the first
 * is how an outage turns into a claim about a person.
 *
 * PRE-141: the two numbers are two different claims and are never merged.
 * `cvEvidence` is what the CV showed — the machine's reading of a document.
 * `rating` is the learner's own band, null until they give it. A CV that merely
 * lists Terraform earns `cvEvidence: 1` and says nothing at all about how well
 * the person knows it; a client that renders the first as the second puts a
 * sentence in someone's mouth.
 */
export const userSkillSchema = z.object({
  id: z.string(),
  skillId: z.string().nullable(),
  rawLabel: z.string(),
  /** The learner's own band. Null until they set it. */
  rating: z.number().int().min(1).max(5).nullable(),
  /** How strongly the CV evidenced it, 1-5. Null when nothing rated it. */
  cvEvidence: z.number().int().min(1).max(5).nullable(),
  source: z.string(),
  approved: z.boolean(),
});
export type UserSkillView = z.infer<typeof userSkillSchema>;

/** PUT /v1/me/skills body — the learner's edits and acceptances. */
export const userSkillDecisionsInputSchema = z.object({
  decisions: z
    .array(
      z
        .object({
          id: z.string().min(1),
          rating: z.number().int().min(1).max(5).optional(),
          approved: z.boolean().optional(),
        })
        // A decision that sets neither field asks for nothing.
        .refine((d) => d.rating != null || d.approved != null, {
          message: 'a decision must set `rating`, `approved`, or both',
        }),
    )
    // PRE-95: an empty list is a real answer — "I looked and changed nothing",
    // which is the only answer available when the CV parsed to no skills at all.
    // Refusing it would block exactly the learners whose CV could not be read.
    .max(100),
});
export type UserSkillDecisionsInput = z.infer<typeof userSkillDecisionsInputSchema>;

/**
 * PRE-267: POST /v1/me/skills — the learner names a skill themselves when the
 * CV parse left them with nothing (or they want to add one the CV missed).
 * `cvEvidence` is never set on this path.
 */
export const addManualUserSkillInputSchema = z.object({
  label: z.string().trim().min(1).max(120),
  rating: z.number().int().min(1).max(5),
  /** Optional catalog id from search; ignored if it does not resolve. */
  skillId: z.string().min(1).optional(),
});
export type AddManualUserSkillInput = z.infer<typeof addManualUserSkillInputSchema>;

/** GET /v1/skills — learner catalog search (id + title + slug only). */
export const skillSearchItemSchema = z.object({
  id: z.string(),
  slug: z.string(),
  title: z.string(),
});
export type SkillSearchItem = z.infer<typeof skillSearchItemSchema>;

export const skillSearchResponseSchema = z.object({
  items: z.array(skillSearchItemSchema),
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  offset: z.number().int().nonnegative(),
});
export type SkillSearchResponse = z.infer<typeof skillSearchResponseSchema>;
